import { EditOutlined, EyeOutlined } from '@ant-design/icons';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Avatar, Button, Form, Input, Modal, Table, Tag, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useState } from 'react';
import CopyableText from '../../components/copyable-text';
import DeviceDetails from '../../components/device-details';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { Device } from '../../types/device.types';
import { RetailerMinimal } from '../../types/retailers.types';
import { UserMinimal } from '../../types/user.types';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const RecoveredDevices = () => {
	const queryClient = useQueryClient();
	const [form] = Form.useForm();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 10,
	});
	const [count, setCount] = useState(0);
	const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
	const [updateModalVisible, setUpdateModalVisible] = useState(false);
	const [currentDeviceId, setCurrentDeviceId] = useState<number | null>(null);
	const [forcedSaleAmount, setForcedSaleAmount] = useState<string>('');
	const [updateLoading, setUpdateLoading] = useState(false);

	const { data, error, isLoading } = useQuery({
		queryKey: ['recovered-devices', paginationConfig.page, paginationConfig.pageSize],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				is_recovered: true,
			};

			const response = await axios.get('/admin/devices', {
				withCredentials: true,
				params,
			});
			setCount(response.data.count);
			return response.data;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const devices: Device[] = data?.results || [];

	const handleUpdateForcedSaleAmount = (device: Device) => {
		setCurrentDeviceId(device.id);
		setForcedSaleAmount(device.forced_sale_paid_amount || '');
		setUpdateModalVisible(true);
	};

	const handleSaveForcedSaleAmount = async () => {
		if (!currentDeviceId) return;

		// Validate the input
		if (!forcedSaleAmount || isNaN(Number(forcedSaleAmount))) {
			message.error('Please enter a valid amount');
			return;
		}

		setUpdateLoading(true);
		try {
			await axios.patch(
				`/admin/devices/edit/${currentDeviceId}`,
				{
					forced_sale_paid_amount: forcedSaleAmount,
				},
				{
					withCredentials: true,
				},
			);
			message.success('Forced sale amount updated successfully');
			queryClient.invalidateQueries({ queryKey: ['recovered-devices'] });
			setUpdateModalVisible(false);
		} catch (err) {
			const error = err as Error;
			handleAPIError(error);
		} finally {
			setUpdateLoading(false);
		}
	};

	const columns: ColumnsType<Device> = [
		{
			title: 'ID',
			dataIndex: 'id',
			key: 'id',
			width: 80,
		},
		{
			title: 'User',
			dataIndex: 'user',
			key: 'user',
			render: (user: UserMinimal) => (
				<div className="flex items-center gap-2">
					<Avatar src={user.dp} />
					<div>
						<span className="text-sm font-medium uppercase">
							{user.first_name} {user.last_name}
						</span>
						<CopyableText text={user.phone_number}>
							<a
								className="text-sm text-gray-500"
								href={`tel:+${user.phone_number}`}
							>
								{user.phone_number}
							</a>
						</CopyableText>
					</div>
				</div>
			),
		},
		{
			title: 'Model',
			dataIndex: 'phone_model',
			key: 'phone_model',
		},
		{
			title: 'IMEI',
			dataIndex: 'imei',
			key: 'imei',
			render: (imei: string) => <CopyableText text={imei}>{imei}</CopyableText>,
		},
		{
			title: 'Retailer',
			dataIndex: 'retailer',
			key: 'retailer',
			render: (retailer: RetailerMinimal) => (
				<div>
					<span className="text-sm font-medium uppercase">{retailer.business_name}</span>
					<CopyableText
						text={retailer.vendor_id}
						className="text-sm text-gray-500"
					/>
				</div>
			),
		},
		{
			title: 'Phone Amount',
			dataIndex: 'phone_amount',
			key: 'phone_amount',
			render: (phone_amount: string) => `${formatNumber(phone_amount)} UGX`,
		},
		{
			title: 'Total Paid',
			dataIndex: 'total_amount_paid',
			key: 'total_amount_paid',
			render: (total_amount_paid: string) => `${formatNumber(total_amount_paid)} UGX`,
		},
		{
			title: 'Forced Sale Paid Amount',
			dataIndex: 'forced_sale_paid_amount',
			key: 'forced_sale_paid_amount',
			render: (forced_sale_paid_amount: string, device: Device) => (
				<div className="flex items-center gap-2">
					<span className="text-sm font-medium">
						{forced_sale_paid_amount ? `${formatNumber(forced_sale_paid_amount)} UGX` : 'Not Set'}
					</span>
					<Button
						type="text"
						size="small"
						icon={<EditOutlined />}
						onClick={() => handleUpdateForcedSaleAmount(device)}
					/>
				</div>
			),
		},
		{
			title: 'Net Loss',
			key: 'net_loss',
			render: (_: string, device: Device) => {
				const totalToBePaid = parseFloat(device.total_amount_to_be_paid) || 0;
				const totalPaid = parseFloat(device.total_amount_paid) || 0;
				const forcedSalePaid = parseFloat(device.forced_sale_paid_amount) || 0;
				const netLoss = totalToBePaid - (totalPaid + forcedSalePaid);

				return <span className={`text-sm font-medium ${netLoss > 0 ? 'text-red-600' : 'text-green-600'}`}>{formatNumber(netLoss)} UGX</span>;
			},
		},
		{
			title: 'Status',
			dataIndex: 'status',
			key: 'status',
			render: (status: string) => (
				<Tag color={status === 'approved' ? 'blue' : status === 'pending' ? 'green' : 'red'}>{status.toUpperCase()}</Tag>
			),
		},
		{
			title: 'Actions',
			dataIndex: 'id',
			key: 'actions',
			render: (_: number, device: Device) => (
				<Button
					type="text"
					icon={<EyeOutlined />}
					onClick={() => setSelectedDevice(device)}
				>
					View
				</Button>
			),
		},
	];

	return (
		<AppLayout
			title="Recovered Devices"
			subtitle="View all recovered devices and manage forced sale amounts"
		>
			<div className="mb-20">
				<Table
					columns={columns}
					dataSource={devices}
					rowKey="id"
					pagination={{
						total: count,
						pageSize: paginationConfig.pageSize,
						current: paginationConfig.page,
						showSizeChanger: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize });
						},
					}}
					loading={isLoading}
					scroll={{ x: 1400 }}
				/>
			</div>

			{selectedDevice && (
				<DeviceDetails
					device={selectedDevice}
					onClose={() => setSelectedDevice(null)}
				/>
			)}

			<Modal
				title="Update Forced Sale Paid Amount"
				open={updateModalVisible}
				onCancel={() => setUpdateModalVisible(false)}
				onOk={handleSaveForcedSaleAmount}
				confirmLoading={updateLoading}
			>
				<Form
					form={form}
					layout="vertical"
				>
					<Form.Item label="Forced Sale Paid Amount (UGX)">
						<Input
							type="number"
							value={forcedSaleAmount}
							onChange={(e) => setForcedSaleAmount(e.target.value)}
							placeholder="Enter forced sale paid amount"
							min={0}
						/>
					</Form.Item>
					<div className="text-sm text-gray-500">
						<p>Current Device ID: {currentDeviceId}</p>
						<p>Enter the amount paid for this recovered device</p>
					</div>
				</Form>
			</Modal>
		</AppLayout>
	);
};

export default RecoveredDevices;
